// DE-BUDGET-PACE: a DE's spend against its limit this period and whether it lasts, in three
// places — the seat drawer's finance section, the DE profile's Budget tab and (compact) the
// chart's seat cards — plus the modal that edits the pace thresholds (this DE's own, or the
// company's defaults under `settings.budget_pace`).
import {
  Badge,
  Button,
  cn,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  InlineBar,
  Input,
  QuickAddDialog,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontalIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyDetail, DeBudget } from "../../api/types";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatMoney } from "../format";
import { companyQuery, keys } from "../queries";
import { deBudgetQuery } from "./agent-queries";

export type BudgetPace = DeBudget["pace"];
export type PaceStatus = BudgetPace["status"];
type PaceSettings = BudgetPace["settings"];
type PaceKey = keyof PaceSettings;

/** What every view needs: the full pace (`/budget`) or the chart's `cost.de_budget`. */
export type PaceSummary = Pick<
  BudgetPace,
  "status" | "spent_minor" | "limit_minor" | "projected_minor" | "runout_at"
>;

/** The platform's thresholds (the API's defaults when neither the company nor the DE sets one). */
export const PACE_DEFAULTS: PaceSettings = {
  alert_at: 1.1,
  min_used: 0.2,
  second_at: 1.3,
  reserve: 0.01,
};

export const PACE_LABELS: Record<PaceStatus, string> = {
  on_track: "On track",
  ahead_of_pace: "Ahead of pace",
  reserve_reached: "Reserve reached",
  no_budget: "No budget",
};

const PACE_TONE: Record<PaceStatus, "success" | "warning" | "danger" | "neutral"> = {
  on_track: "success",
  ahead_of_pace: "warning",
  reserve_reached: "danger",
  no_budget: "neutral",
};

/** "Nov 12". */
function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const pct = (f: number) => `${Math.round(f * 1000) / 10}%`;

export function BudgetPaceChip({ status }: { status: PaceStatus }) {
  if (status === "no_budget") return null;
  return (
    <Badge variant={PACE_TONE[status]} dot data-slot="budget-pace-chip" data-status={status}>
      {PACE_LABELS[status]}
    </Badge>
  );
}

/** The seat card's compact mark: only when the pace needs a look (on track stays quiet). */
export function BudgetPaceMark({ pace, currency }: { pace: PaceSummary; currency: string }) {
  if (pace.status !== "ahead_of_pace" && pace.status !== "reserve_reached") return null;
  const reserve = pace.status === "reserve_reached";
  const title = `DE budget: ${PACE_LABELS[pace.status]}${
    pace.limit_minor !== null
      ? `, ${formatMoney(pace.spent_minor, currency, { whole: true })} of ${formatMoney(pace.limit_minor, currency, { whole: true })}`
      : ""
  }${!reserve && pace.runout_at ? `, runs out ~${shortDay(pace.runout_at)}` : ""}`;
  return (
    <span
      data-slot="budget-pace-mark"
      data-status={pace.status}
      title={title}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 text-2xs font-medium whitespace-nowrap",
        reserve ? "text-danger-text" : "text-warning-text",
      )}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      <span aria-hidden="true">{reserve ? "Reserve" : "Ahead"}</span>
      <span className="sr-only">{title}</span>
    </span>
  );
}

function paceInfo(settings: PaceSettings | null): ReactNode {
  const s = settings ?? PACE_DEFAULTS;
  return (
    <>
      Projected = spend so far ÷ share of the period elapsed (the last 7 days' pace after day 7).
      Alerts at {pct(s.alert_at)} once {pct(s.min_used)} is spent, again past {pct(s.second_at)}.
      The last {pct(s.reserve)} is held back.
    </>
  );
}

/**
 * The "Budget" KPI row: spent of limit, a bar, the pace chip, and when ahead of pace the
 * projection and run-out day. `settings` (the full pace's) fills the (i); `action` is the
 * row's hover icon (the thresholds modal).
 */
export function BudgetPaceLine({
  pace,
  currency,
  settings = null,
  action,
  label = "Budget",
}: {
  pace: PaceSummary;
  currency: string;
  settings?: PaceSettings | null;
  action?: ReactNode;
  label?: string;
}) {
  const limit = pace.limit_minor;
  const used = limit && limit > 0 ? (pace.spent_minor / limit) * 100 : null;
  return (
    <div
      className="group/pace flex min-w-0 flex-col gap-1.5"
      data-slot="budget-pace"
      data-status={pace.status}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm tabular-nums">
        <span className="text-xs text-faint">{label}</span>
        {pace.status === "no_budget" || limit === null ? (
          <span className="text-xs text-faint">No budget</span>
        ) : (
          <>
            <span className="font-medium">
              {formatMoney(pace.spent_minor, currency, { whole: true })}
              <span className="font-normal text-muted-foreground">
                {" "}
                of {formatMoney(limit, currency, { whole: true })}
              </span>
            </span>
            <BudgetPaceChip status={pace.status} />
          </>
        )}
        <InfoTip label="About the budget pace" className="size-4">
          {paceInfo(settings)}
        </InfoTip>
        {action ? <span className="ml-auto flex items-center">{action}</span> : null}
      </div>
      {used !== null ? (
        <InlineBar
          value={Math.min(100, used)}
          size="sm"
          tone={
            pace.status === "reserve_reached"
              ? "danger"
              : pace.status === "ahead_of_pace"
                ? "warning"
                : "primary"
          }
          aria-hidden="true"
        />
      ) : null}
      {pace.status === "ahead_of_pace" && (pace.projected_minor !== null || pace.runout_at) ? (
        <p className="text-xs text-warning-text tabular-nums" data-slot="budget-pace-projection">
          {pace.projected_minor !== null
            ? `~${formatMoney(pace.projected_minor, currency, { whole: true })} projected`
            : null}
          {pace.projected_minor !== null && pace.runout_at ? " · " : null}
          {pace.runout_at ? `Runs out ~${shortDay(pace.runout_at)}` : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The seat drawer's DE budget row: a manager or a person above the DE's seat reads the full pace
 * (`/budget`; the settings icon is a manager's); anyone else who reads the seat's cost sees the
 * chart's `de_budget`.
 */
export function DeBudgetPaceRow({
  api,
  company,
  deId,
  deName,
  summary,
  currency,
  manager,
  reader = manager,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  deName: string;
  summary: PaceSummary | undefined;
  currency: string;
  manager: boolean;
  /** Reads the full pace: a manager, or a person above the DE's seat (its supervisors). */
  reader?: boolean;
}) {
  const full = useQuery({ ...deBudgetQuery(api, company.id, deId), enabled: reader });
  const [open, setOpen] = useState(false);
  const pace = full.data?.pace;
  const shown: PaceSummary | undefined = pace ?? summary;
  if (!shown) return null;
  return (
    <div data-slot="seat-de-budget" className="flex flex-col gap-1">
      <BudgetPaceLine
        pace={shown}
        currency={pace?.currency ?? currency}
        settings={pace?.settings ?? null}
        action={manager ? <BudgetPaceSettingsButton onClick={() => setOpen(true)} /> : undefined}
      />
      {full.data ? <DeBudgetFacts data={full.data} currency={pace?.currency ?? currency} /> : null}
      {open ? (
        <BudgetPaceSettingsDialog
          api={api}
          company={company}
          deId={deId}
          deName={deName}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * BUDGET-CAP: the drawer's budget facts in one small line — spend this period and the cap in
 * exact money (cents, never rounded: amounts are minor units), the mode, and the next reset.
 */
export function DeBudgetFacts({ data, currency }: { data: DeBudget; currency: string }) {
  if (data.mode === null || data.cap_minor === null) return null;
  const spent = data.pace.spent_minor;
  const facts = [
    `${formatMoney(spent, currency)} spent this period`,
    `${data.mode === "cap" ? "cap" : "limit"} ${formatMoney(data.cap_minor, currency)}`,
    data.mode === "cap" ? "Cap: new work stops at the cap" : "Alert only: never stops",
    data.resets_at ? `resets ${shortDay(data.resets_at)}` : "no reset (standing)",
  ];
  return (
    <p
      className="text-xs text-muted-foreground tabular-nums"
      data-slot="seat-de-budget-facts"
      data-mode={data.mode}
    >
      {facts.join(" · ")}
      {data.paused_for_budget ? (
        <span className="text-danger-text"> · paused: budget reached</span>
      ) : null}
    </p>
  );
}

/** The thresholds modal's opener: a hover icon (always on touch and keyboard focus). */
export function BudgetPaceSettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label="Budget pace settings"
      title="Budget pace settings"
      data-slot="budget-pace-settings"
      onClick={onClick}
      className="text-faint opacity-0 group-hover/pace:opacity-100 hover:text-foreground focus-visible:opacity-100 pointer-coarse:opacity-100"
    >
      <SlidersHorizontalIcon aria-hidden="true" />
    </Button>
  );
}

const FIELDS: { key: PaceKey; label: string; slot: string }[] = [
  { key: "alert_at", label: "Alert at", slot: "pace-alert-at" },
  { key: "min_used", label: "Only after", slot: "pace-min-used" },
  { key: "second_at", label: "Second alert", slot: "pace-second-at" },
  { key: "reserve", label: "Reserve", slot: "pace-reserve" },
];

type Draft = Record<PaceKey, string>;
type Partial4 = { [K in PaceKey]?: number | undefined };

const draftOf = (v: Partial4 | undefined): Draft => ({
  alert_at: v?.alert_at === undefined ? "" : String(Math.round(v.alert_at * 1000) / 10),
  min_used: v?.min_used === undefined ? "" : String(Math.round(v.min_used * 1000) / 10),
  second_at: v?.second_at === undefined ? "" : String(Math.round(v.second_at * 1000) / 10),
  reserve: v?.reserve === undefined ? "" : String(Math.round(v.reserve * 1000) / 10),
});

/** The typed percents as fractions; an empty field is left out (it inherits). */
function valuesOf(d: Draft): Partial<PaceSettings> | null {
  const out: Partial<PaceSettings> = {};
  for (const { key } of FIELDS) {
    const raw = d[key].trim().replace(/%$/, "").trim();
    if (raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    out[key] = Math.round(n * 100) / 10_000;
  }
  return out;
}

function companyDefaultsOf(detail: CompanyDetail | undefined): PaceSettings {
  const bp = detail?.settings.budget_pace;
  return {
    alert_at: bp?.alert_at ?? PACE_DEFAULTS.alert_at,
    min_used: bp?.min_used ?? PACE_DEFAULTS.min_used,
    second_at: bp?.second_at ?? PACE_DEFAULTS.second_at,
    reserve: bp?.reserve ?? PACE_DEFAULTS.reserve,
  };
}

function problemsOf(v: PaceSettings): Partial<Record<PaceKey, string>> {
  const p: Partial<Record<PaceKey, string>> = {};
  if (v.alert_at < 1 || v.alert_at > 5) p.alert_at = "100% to 500%.";
  if (v.min_used < 0 || v.min_used > 1) p.min_used = "0% to 100%.";
  if (v.second_at < v.alert_at) p.second_at = "At least the first alert.";
  else if (v.second_at > 10) p.second_at = "At most 1000%.";
  if (v.reserve < 0 || v.reserve > 0.5) p.reserve = "0% to 50%.";
  return p;
}

/**
 * The pace thresholds for one DE (`settings.budget_pace.for_de[deId]`, an empty field inherits
 * the company default shown as its placeholder), or the company defaults. The save changes only
 * that scope on the server (`companies.updateBudgetPace`), so concurrent saves keep each other's.
 */
export function BudgetPaceSettingsDialog({
  api,
  company,
  deId,
  deName,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  deName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const id = useId();
  const detail = useQuery(companyQuery(api, company.id));
  const [scope, setScope] = useState<"de" | "company">("de");
  const [drafts, setDrafts] = useState<Record<"de" | "company", Draft> | null>(null);
  if (detail.data && drafts === null) {
    const bp = detail.data.settings.budget_pace;
    const { for_de: _forDe, ...top } = bp ?? {};
    setDrafts({ de: draftOf(bp?.for_de?.[deId]), company: draftOf(top) });
  }
  const companyDefaults = companyDefaultsOf(detail.data);
  const fallback = scope === "de" ? companyDefaults : PACE_DEFAULTS;
  const draft = drafts?.[scope] ?? draftOf(undefined);
  const values = valuesOf(draft);
  const effective: PaceSettings | null = values ? { ...fallback, ...values } : null;
  const problems = effective ? problemsOf(effective) : {};
  const valid = drafts !== null && values !== null && Object.keys(problems).length === 0;
  const empty = FIELDS.every(({ key }) => draft[key].trim() === "");

  const save = useMutation({
    mutationFn: async () => {
      if (!drafts) throw new Error("Not loaded yet.");
      return api.client.companies.updateBudgetPace({
        params: { co: company.id },
        body: {
          ...(scope === "de" ? { de_membership_id: deId } : {}),
          settings: valuesOf(drafts[scope]) ?? {},
        },
      });
    },
    onSuccess: () => {
      toast.add({ title: "Pace settings saved", type: "success" });
      onClose();
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.company(company.id) });
      // Every DE's pace reads the company default; the chart's cards show it too.
      void qc.invalidateQueries({ queryKey: ["company", company.id, "de"] });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] });
    },
  });

  const setField = (key: PaceKey, value: string) =>
    setDrafts((d) => (d ? { ...d, [scope]: { ...d[scope], [key]: value } } : d));

  return (
    <QuickAddDialog
      title="Budget pace"
      info={paceInfo(effective)}
      description={scope === "de" ? deName : "Company default"}
      submitLabel="Save"
      canSubmit={valid}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="budget-pace-dialog"
      error={
        save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(save.error, "The pace settings could not be saved.")}
          </p>
        ) : detail.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(detail.error, "The company settings could not be loaded.")}
          </p>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex items-center gap-1" data-slot="budget-pace-scope">
          <legend className="sr-only">Applies to</legend>
          {(["de", "company"] as const).map((s) => (
            <Button
              key={s}
              type="button"
              size="xs"
              variant={scope === s ? "secondary" : "ghost"}
              aria-pressed={scope === s}
              onClick={() => setScope(s)}
            >
              {s === "de" ? "This DE" : "Company default"}
            </Button>
          ))}
        </fieldset>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map(({ key, label, slot }) => (
            <Field key={key} data-invalid={problems[key] ? true : undefined}>
              <FieldLabel htmlFor={`${id}-${key}`}>{label}</FieldLabel>
              <div className="flex items-center gap-1.5">
                <Input
                  id={`${id}-${key}`}
                  inputMode="decimal"
                  data-slot={slot}
                  disabled={drafts === null}
                  value={draft[key]}
                  placeholder={String(Math.round(fallback[key] * 1000) / 10)}
                  aria-invalid={problems[key] ? true : undefined}
                  onChange={(e) => setField(key, e.target.value)}
                />
                <span className="text-sm text-muted-foreground" aria-hidden="true">
                  %
                </span>
              </div>
              {problems[key] ? <FieldError>{problems[key]}</FieldError> : null}
            </Field>
          ))}
        </div>
        {values === null ? <FieldError>Numbers only.</FieldError> : null}
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="self-start"
          disabled={empty || drafts === null}
          data-slot="budget-pace-reset"
          onClick={() => setDrafts((d) => (d ? { ...d, [scope]: draftOf(undefined) } : d))}
        >
          {scope === "de" ? "Use company default" : "Use platform default"}
        </Button>
      </div>
    </QuickAddDialog>
  );
}
